import { ScheduleRecordEventRequest } from "@/types/portalFunctionTypes";
import { axiosClient } from "../axiosClient";
import {
  GetMyMeetingsResponse,
  MyMeetingData,
  CalendarInfo,
  CalendarEventsRequestBody,
  CalendarEventsResponse,
} from "@freetech/models/meetings";
import { RetranscribeBotRequest } from "@/types/portalFunctionTypes";

export const meetingbot = {
  admin: {
    generateGoogleOAuthLink: async (): Promise<{
      success: boolean;
      authUrl?: string;
      state?: string;
      error?: string;
    }> => {
      return await axiosClient.get(
        "/meetingbot/admin/generate-google-oauth-link"
      );
    },

    getCalendar: async (uuid: string): Promise<CalendarInfo> => {
      return await axiosClient.get(`/meetingbot/admin/get-calendar/${uuid}`);
    },

    getCalendarEvents: async (
      requestBody: CalendarEventsRequestBody
    ): Promise<CalendarEventsResponse> => {
      return await axiosClient.post(
        "/meetingbot/admin/get-calendar-events",
        requestBody
      );
    },

    resyncAllCalendars: async () => {
      return await axiosClient.post("/meetingbot/admin/resync-all-calendars");
    },

    updateCalendar: async (uuid: string, refreshToken: string) => {
      return await axiosClient.patch(
        `/meetingbot/admin/update-calendar/${uuid}`,
        {
          refreshToken,
        }
      );
    },

    scheduleRecordEvent: async ({
      eventUuid,
      allOccurrences = false,
      botConfig,
    }: ScheduleRecordEventRequest) => {
      return await axiosClient.post("/meetingbot/admin/schedule-record-event", {
        eventUuid,
        allOccurrences,
        botConfig,
      });
    },

    updateRecordEvent: async ({
      eventUuid,
      allOccurrences = false,
      botConfig,
    }: ScheduleRecordEventRequest) => {
      return await axiosClient.patch("/meetingbot/admin/update-record-event", {
        eventUuid,
        allOccurrences,
        botConfig,
      });
    },

    joinCalendarMeeting: async ({
      meetingUrl,
      eventUuid,
      botName = "FreeTech Meeting Bot",
      reserved = false,
      includeTranscripts = true,
    }: {
      meetingUrl: string;
      eventUuid: string;
      botName?: string;
      reserved?: boolean;
      includeTranscripts?: boolean;
    }) => {
      return await axiosClient.post("/meetingbot/admin/join-calendar-meeting", {
        meetingUrl,
        eventUuid,
        botName,
        reserved,
        includeTranscripts,
      });
    },

    createOneOffBot: async ({
      meetingUrl,
      botName,
    }: {
      meetingUrl: string;
      botName: string;
    }) => {
      return await axiosClient.post("/meetingbot/admin/create-one-off-bot", {
        meetingUrl,
        botName,
      });
    },
    getBots: async (params?: {
      bot_name?: string;
      created_after?: string;
      created_before?: string;
      cursor?: string;
      ended_after?: string;
      filter_by_extra?: string;
      limit?: number;
      meeting_url?: string;
      sort_by_extra?: string;
      speaker_name?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const fullUrl = queryParams.toString()
        ? `/meetingbot/admin/bots?${queryParams.toString()}`
        : "/meetingbot/admin/bots";

      return await axiosClient.get(fullUrl);
    },

    getMeetingData: async (
      botId: string,
      includeTranscripts: boolean = true,
      includeScreenshots: boolean = false
    ) => {
      const queryParams = new URLSearchParams({
        bot_id: botId,
        include_transcripts: includeTranscripts.toString(),
        include_screenshots: includeScreenshots.toString(),
      });

      return await axiosClient.get(
        `/meetingbot/admin/meeting-data?${queryParams.toString()}`
      );
    },

    retranscribeBot: async (params: {
      bot_uuid: string;
      speech_to_text?: {
        api_key?: string;
        provider?: string;
      };
      webhook_url?: string;
    }) => {
      const requestBody: RetranscribeBotRequest = {
        bot_uuid: params.bot_uuid,
        speech_to_text: params.speech_to_text,
        webhook_url: params.webhook_url,
      };

      return await axiosClient.post(
        "/meetingbot/admin/retranscribe-bot",
        requestBody
      );
    },

    leaveBot: async (botId: string) => {
      return await axiosClient.delete(`/meetingbot/admin/leave/${botId}`);
    },

    generateMeetingOverview: async (params: {
      botUuid: string;
      attendeeEmails?: string[];
      startTime?: number;
      guidance?: string;
      model?: string;
    }) => {
      return await axiosClient.post("/meetingbot/admin/generate-overview", {
        botUuid: params.botUuid,
        attendeeEmails: params.attendeeEmails,
        startTime: params.startTime,
        guidance: params.guidance,
        model: params.model,
      });
    },

    getMeetingOverview: async (botUuid: string) => {
      return await axiosClient.get(
        `/meetingbot/admin/get-meeting-overview/${botUuid}`
      );
    },

    createPortalMeeting: async ({
      startTime,
      endTime,
      attendeeName,
      attendeeEmail,
      attendeeCompany,
      description,
      timezone,
    }: {
      startTime: string;
      endTime: string;
      attendeeName: string;
      attendeeEmail: string;
      attendeeCompany?: string;
      description?: string;
      timezone?: string;
    }) => {
      return await axiosClient.post("/meetingbot/admin/create-portal-meeting", {
        startTime,
        endTime,
        attendeeName,
        attendeeEmail,
        attendeeCompany,
        description,
        timezone,
      });
    },

    rescheduleSchedulingLink: async ({
      schedulingLinkId,
      newStartTime,
      newEndTime,
      timezone,
    }: {
      schedulingLinkId: string;
      newStartTime: string;
      newEndTime: string;
      timezone?: string;
    }) => {
      return await axiosClient.post(
        "/meetingbot/admin/reschedule-scheduling-link",
        {
          schedulingLinkId,
          newStartTime,
          newEndTime,
          timezone,
        }
      );
    },

    cancelSchedulingLink: async (
      schedulingLinkId: string,
      cancellationReason?: string
    ) => {
      return await axiosClient.post(
        "/meetingbot/admin/cancel-scheduling-link",
        {
          schedulingLinkId,
          cancellationReason,
        }
      );
    },
  },
  shared: {
    getMyMeetings: async (): Promise<GetMyMeetingsResponse> => {
      return await axiosClient.get("/meetingbot/shared/meetings");
    },

    getMeetingById: async (
      meetingId: string
    ): Promise<{ success: boolean; data: MyMeetingData; error?: string }> => {
      return await axiosClient.get(`/meetingbot/shared/meeting/${meetingId}`);
    },
  },
};
